import { useCallback, useRef, useState } from 'react';

import type { TagApply } from './TagPalette';
import type { TagEdit } from './TagManagerDialog';
import type { NewGuestLink } from './GuestLinksTile';
import {
  assignTags,
  conflictingLink,
  createGuestLink,
  createTag,
  deleteGuestLink,
  deleteTag,
  patchTag,
  seedHealthVocabulary,
  tagFailureMessage,
  tagInUse,
  type TagResult,
} from '../../../services/tags-api-client';
import { describeLink } from '../../../lib/guestLinks';
import {
  assignOutcomeSentence,
  newTagRequest,
  type CreateTagKind,
} from '../../../lib/guestTags';
import type { TrackerBoard } from '../../../services/tracker-api-client';

export interface GuestTagWrites {
  busy: boolean;
  /** The last refusal, in the host's words. '' when the last write went through. */
  error: string;
  clearError: () => void;
  assign: (apply: TagApply, inviteeIds: string[]) => Promise<boolean>;
  /**
   * Puts the seeded dietary and access vocabulary on this trip if it is not
   * there yet. Safe to call every time the picker opens — it fires once per
   * trip — and it never reports anything, because nothing on screen is waiting
   * on it.
   */
  ensureHealthVocabulary: () => void;
  create: (label: string, kind: CreateTagKind) => Promise<void>;
  edit: (tagId: string, edit: TagEdit) => Promise<void>;
  remove: (tagId: string, force: boolean) => Promise<void>;
  /** False when the write was refused, so the form can keep what the host typed. */
  createLink: (inviteeId: string, link: NewGuestLink) => Promise<boolean>;
  deleteLink: (linkId: string) => Promise<void>;
}

/**
 * Every tag and link write, and what each one does to the board already on
 * screen.
 *
 * Two decisions are worth naming.
 *
 * **The response is the truth, not an optimistic guess.** `POST /tags/assign`
 * answers with the updated vocabulary AND, per affected guest, the tag ids they
 * now carry, precisely so this needs no re-read and no local re-derivation of
 * who holds what. Guessing would put a second copy of the server's arithmetic
 * in the browser — which guests already held the tag, which were skipped — and
 * the numbers the host reads afterwards would be that copy's opinion rather
 * than an account of what was written.
 *
 * **Every write takes a ticket first.** The board's polling loop drops any
 * background read whose ticket has been superseded; a bulk tag write that did
 * not take one could be reverted on screen by a GET that was already in flight
 * when it started. `beginWrite` is that ticket — the same discipline the row
 * patches already run under, not a second one.
 */
export function useGuestTags({
  groupId,
  updateBoard,
  beginWrite,
  endWrite,
  onNotice,
}: {
  groupId: string;
  updateBoard: (mutate: (board: TrackerBoard) => TrackerBoard) => void;
  /** Takes a write ticket, so an in-flight background read cannot land on top. */
  beginWrite: () => void;
  endWrite: () => void;
  onNotice: (notice: { tone: 'success' | 'error'; message: string }) => void;
}): GuestTagWrites {
  /*
    A COUNT of writes in flight, not a flag. Two tag writes overlap in ordinary
    use — the manager dialog commits the name on blur and the kind on the very
    next click — and a boolean is cleared by whichever finishes first, which
    re-enables the dialog while the other is still on the wire.
  */
  const [writesInFlight, setWritesInFlight] = useState(0);
  const [error, setError] = useState('');

  /**
   * The one place a tag write is run, so the ticket, the busy flag and the
   * error message can never be got right in three places and wrong in a fourth.
   * Returns the result so each caller can splice its own shape of answer.
   */
  const run = useCallback(
    async <T,>(call: () => Promise<TagResult<T>>): Promise<TagResult<T>> => {
      setWritesInFlight((count) => count + 1);
      setError('');
      beginWrite();
      const result = await call();
      endWrite();
      setWritesInFlight((count) => Math.max(0, count - 1));
      if (!result.ok) setError(tagFailureMessage(result.failure));
      return result;
    },
    [beginWrite, endWrite],
  );

  const assign = useCallback(
    async (apply: TagApply, inviteeIds: string[]) => {
      const result = await run(() =>
        assignTags(groupId, {
          tagIds: apply.tagIds,
          inviteeIds,
          mode: apply.mode,
          ...(apply.note ? { note: apply.note } : {}),
        }),
      );
      if (!result.ok) return false;

      /*
        The server answers with affected-guest-id -> the tag ids they now carry,
        not with whole guest rows: at 500 guests, echoing the people back is the
        payload this response exists to avoid. So splice the tag ids onto the
        rows already in hand and leave every other field of the guest alone —
        the board's own writes (a name being edited in another cell) must not be
        clobbered by a tag apply.
      */
      const saved = result.data.guests ?? {};
      updateBoard((board) => ({
        ...board,
        tags: result.data.tags,
        guests: board.guests.map((guest) =>
          saved[guest.inviteeId] ? { ...guest, tagIds: saved[guest.inviteeId] } : guest),
      }));

      /*
        What MOVED, not what was targeted, and in the units the server counts:
        `assigned`/`removed`/`skipped` are (guest × tag) pairs, not people. The
        sentence is composed in the library, beside the one the host read BEFORE
        the click, so the two cannot drift into contradicting each other.
      */
      onNotice({ tone: 'success', message: assignOutcomeSentence(result.data) });
      return true;
    },
    [groupId, run, updateBoard, onNotice],
  );

  /**
   * The trip whose health vocabulary has already been asked for, so opening the
   * picker twenty times in one sitting is one request. A ref rather than state
   * because nothing renders from it.
   */
  const healthVocabularyAsked = useRef<string | null>(null);

  /**
   * Inserts the seeded allergy, diet and mobility vocabulary, once per trip.
   *
   * The endpoint is lazy by design — twenty-odd rows on every trip ever created
   * is worse than twenty rows on the trips that open the picker — so something
   * has to say "the picker is open", and this is it.
   *
   * It deliberately sits outside `run`. It takes no busy flag, so the Apply
   * button is never disabled by a call the host did not make; it sets no error,
   * because a host who opened the palette to type "college friends" should not
   * be told a seeding call they never asked for failed; and it is not awaited,
   * so the palette draws from the vocabulary already in hand and fills in when
   * the seeds land. It still takes a write ticket: the splice below is a board
   * write like any other, and a poll already in flight must not land on top.
   */
  const ensureHealthVocabulary = useCallback(() => {
    if (healthVocabularyAsked.current === groupId) return;
    healthVocabularyAsked.current = groupId;
    void (async () => {
      beginWrite();
      const result = await seedHealthVocabulary(groupId);
      endWrite();
      if (!result.ok) {
        // A route this API does not have will not appear before the next
        // reload, so asking again on every open is just noise. Anything else —
        // a dropped connection, a 500 — is worth one more try when the host
        // reopens the picker.
        if (result.failure.kind !== 'unsupported') healthVocabularyAsked.current = null;
        return;
      }
      // The route answers with the dietary and access tags ONLY, so this merges
      // by id and leaves every social tag alone. Replacing `tags` wholesale
      // would delete "college friends" off the palette on the way past.
      const seeded = result.data.tags ?? [];
      if (seeded.length === 0) return;
      const seededIds = new Set(seeded.map((tag) => tag.id));
      updateBoard((board) => ({
        ...board,
        tags: [...(board.tags ?? []).filter((tag) => !seededIds.has(tag.id)), ...seeded],
      }));
    })();
  }, [groupId, beginWrite, endWrite, updateBoard]);

  const create = useCallback(
    async (label: string, kind: CreateTagKind) => {
      // `newTagRequest` omits `kind` on Auto, which is the only way the
      // server's seeded definition for a slug like "wheelchair" survives.
      const result = await run(() => createTag(groupId, newTagRequest(label, kind)));
      if (!result.ok) return;
      const { tag, created } = result.data;
      updateBoard((board) => ({
        ...board,
        // Idempotent on slug: an existing tag comes back unchanged, so the
        // splice replaces rather than appends and a twin cannot appear.
        tags: [...(board.tags ?? []).filter((existing) => existing.id !== tag.id), tag],
      }));
      if (!created) {
        onNotice({ tone: 'success', message: `“${tag.label}” was already a tag on this trip.` });
      }
    },
    [groupId, run, updateBoard, onNotice],
  );

  /**
   * The newest edit issued per tag, so the LAST REQUEST wins rather than the
   * last response — the same ticket the row patches take, for the same reason.
   * The manager dialog commits one field per request, so renaming a tag and
   * then retyping it is two overlapping writes to one row by design; without
   * this the rename's answer, which still carries the old kind, can land second
   * and put the old kind back on screen for the next thirty seconds.
   */
  const editSeq = useRef(0);
  const latestEdit = useRef<Record<string, number>>({});

  const edit = useCallback(
    async (tagId: string, patch: TagEdit) => {
      editSeq.current += 1;
      const issued = editSeq.current;
      latestEdit.current[tagId] = issued;
      const result = await run(() => patchTag(groupId, tagId, patch));
      if (!result.ok) return;
      if (latestEdit.current[tagId] !== issued) return;
      const saved = result.data;
      updateBoard((board) => ({
        ...board,
        tags: (board.tags ?? []).map((tag) => (tag.id === saved.id ? saved : tag)),
      }));
    },
    [groupId, run, updateBoard],
  );

  const remove = useCallback(
    async (tagId: string, force: boolean) => {
      const result = await run(() => deleteTag(groupId, tagId, force));
      if (!result.ok) {
        // The refusal carries the count, so it is turned into the sentence a
        // host can act on rather than a bare 409.
        const inUse = tagInUse(result.failure);
        if (inUse) {
          setError(
            `${inUse.assignedCount} ${inUse.assignedCount === 1 ? 'guest still has' : 'guests still have'} that tag. Delete it off them too, or take it off them first.`,
          );
        }
        return;
      }
      updateBoard((board) => ({
        ...board,
        tags: (board.tags ?? []).filter((tag) => tag.id !== tagId),
        // The server cascaded the join rows; mirroring it here keeps the chips
        // from rendering a tag the vocabulary no longer holds.
        guests: board.guests.map((guest) =>
          guest.tagIds?.includes(tagId)
            ? { ...guest, tagIds: guest.tagIds.filter((id) => id !== tagId) }
            : guest,
        ),
      }));
    },
    [groupId, run, updateBoard],
  );

  const createLink = useCallback(
    async (inviteeId: string, link: NewGuestLink) => {
      const result = await run(() =>
        // The server canonicalizes the pair ordering, so these two go out in
        // whatever order the host picked them.
        createGuestLink(groupId, {
          inviteeId,
          otherId: link.otherId,
          relation: link.relation,
          seating: link.seating,
        }),
      );
      if (!result.ok) {
        /*
          The picker hides everyone this guest is already linked to, so a 409
          here means another host joined the pair while this record was open.
          The refusal carries the stored row, so put it on the record and say
          what it holds: the host can then read it and Unlink it, instead of
          being told the pair is linked and left to find a link that is not on
          their screen.
        */
        const existing = conflictingLink(result.failure);
        if (existing) {
          updateBoard((board) => ({
            ...board,
            links: [...(board.links ?? []).filter((link) => link.id !== existing.id), existing],
          }));
          setError(
            `Another host linked them already — ${describeLink(existing)}. `
              + 'It is on the record now; Unlink it if that is wrong.',
          );
        }
        return false;
      }
      const saved = result.data;
      updateBoard((board) => ({
        ...board,
        links: [...(board.links ?? []).filter((existing) => existing.id !== saved.id), saved],
      }));
      return true;
    },
    [groupId, run, updateBoard],
  );

  const deleteLink = useCallback(
    async (linkId: string) => {
      const result = await run(() => deleteGuestLink(groupId, linkId));
      if (!result.ok) return;
      updateBoard((board) => ({
        ...board,
        links: (board.links ?? []).filter((link) => link.id !== linkId),
      }));
    },
    [groupId, run, updateBoard],
  );

  return {
    busy: writesInFlight > 0,
    error,
    clearError: useCallback(() => setError(''), []),
    assign,
    ensureHealthVocabulary,
    create,
    edit,
    remove,
    createLink,
    deleteLink,
  };
}
